import { useQuery } from '@tanstack/vue-query';
import { toValue, computed, type MaybeRefOrGetter } from 'vue';
import { useUserState } from '@/stores/useUserState';
import {
  getCategoryTree,
  getProducts,
  getContractList,
  getClosingContracts,
  getProductConditional,
} from './prediction.api';
import type {
  PredictionProductsParams,
  PredictionContractListParams,
  PredictionProductConditionalParams,
} from '@/types/prediction.types';
import {
  filterClosingToForecastX,
  filterContractListToForecastX,
  filterPredictionProductsToForecastX,
} from '@/utils/filterToForecastX';

export const useGetPredictionCategoryTree = (enabled: MaybeRefOrGetter<boolean> = true) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => ['prediction', 'category', 'tree', userState.usePublicApi]),
    queryFn: () => getCategoryTree(userState.usePublicApi),
    enabled: () => toValue(enabled),
    staleTime: 30 * 60 * 1000, // 30 minutes - category tree is relatively static
  });
};

export const useGetPredictionProducts = (
  params: MaybeRefOrGetter<PredictionProductsParams>,
  options?: { enabled?: MaybeRefOrGetter<boolean> },
) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => [
      'prediction',
      'product',
      toValue(params).endpoint,
      toValue(params),
      userState.usePublicApi,
    ]),
    queryFn: () => getProducts(toValue(params), userState.usePublicApi),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 1000,
    retry: false,
    enabled: () => {
      if (options?.enabled !== undefined && !toValue(options.enabled)) return false;
      return !!toValue(params).endpoint;
    },
    select: filterPredictionProductsToForecastX,
  });
};

export const useGetPredictionContractList = (
  params: MaybeRefOrGetter<PredictionContractListParams>,
  options?: { enabled?: MaybeRefOrGetter<boolean> },
) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => ['prediction', 'contract', 'list', toValue(params), userState.usePublicApi]),
    queryFn: () => getContractList(toValue(params), userState.usePublicApi),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: () => {
      if (options?.enabled !== undefined && !toValue(options.enabled)) return false;
      const p = toValue(params);
      return !!p.conid && p.conid > 0 && !!p.exchange;
    },
    select: filterContractListToForecastX,
  });
};

export const useGetProductConditional = (
  params: MaybeRefOrGetter<PredictionProductConditionalParams>,
  options?: { enabled?: MaybeRefOrGetter<boolean> },
) => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => ['prediction', 'product', 'conditional', toValue(params), userState.usePublicApi]),
    queryFn: () => getProductConditional(toValue(params), userState.usePublicApi),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 1000,
    retry: false,
    enabled: () => {
      if (options?.enabled !== undefined && !toValue(options.enabled)) return false;
      const p = toValue(params);
      return !!p.conid && p.conid > 0 && !!p.exchange;
    },
    select: filterPredictionProductsToForecastX,
  });
};

export const useGetClosingContracts = () => {
  const userState = useUserState();

  return useQuery({
    queryKey: computed(() => ['prediction', 'contract', 'closing', userState.usePublicApi]),
    queryFn: () => getClosingContracts(userState.usePublicApi),
    staleTime: 1 * 60 * 1000, // 1 minute - closing contracts change frequently
    refetchInterval: 2 * 60 * 1000, // 2 minutes
    refetchIntervalInBackground: false,
    select: filterClosingToForecastX,
  });
};
// last modified by AI tools on 2026-10-09
